import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import {
    getProfitMonitorInsight,
    getProfitMonitors,
    startProfitMonitor,
    updateProfitMonitor
} from '../../services/ProfitMonitorsService';
import ProfitPieChart from './ProfitPieChart';
import ProfitGoalBar from './ProfitGoalBar';
import { formatProfitPct, profitModeHelp, resolveProfitProfile, reversalNeedsBoth } from '../../utils/profitProtect';
import './ProfitMonitor.css';

const INTERVALS = [
    { value: '1m', label: '1 minuto' },
    { value: '5m', label: '5 minutos' },
    { value: '15m', label: '15 minutos' },
    { value: '1h', label: '1 hora' }
];

const INDICATOR_HELP = {
    rsi: 'O RSI mostra se o ativo está barato (sobrevendido) ou caro (sobrecomprado) no curto prazo. Abaixo de 40 favorece compra; acima de 70 sugere espera ou venda.',
    macd: 'O MACD mede o momentum. Quando a linha MACD fica acima da linha de sinal, a tendência de alta ganha força — é um dos gatilhos do sinal verde.',
    ema: 'A EMA 20 acima da EMA 50 indica que o preço médio recente está subindo. Preço acima da EMA 20 reforça a tendência de compra.',
    bb: 'As Bandas de Bollinger marcam extremos. Preço na banda inferior costuma preceder um recuo de alta; na superior, um recuo de baixa.'
};

const PIE_COLORS = {
    bullish: '#4caf50',
    bearish: '#ef5350',
    neutral: '#9e9e9e'
};

const INDICATOR_PIE_KEY = [
    { color: PIE_COLORS.bullish, label: 'Verde: favorece compra' },
    { color: PIE_COLORS.bearish, label: 'Vermelho: favorece venda' }
];

function formatPrice(value) {
    const number = parseFloat(value || 0);
    if (!number) return '--';
    return number.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: number >= 1 ? 4 : 8
    });
}

function formatCompactUsd(value) {
    const number = Number(value);
    if (!number && number !== 0) return '--';
    if (number >= 1e12) return `US$ ${(number / 1e12).toFixed(2)} tri`;
    if (number >= 1e9) return `US$ ${(number / 1e9).toFixed(2)} bi`;
    if (number >= 1e6) return `US$ ${(number / 1e6).toFixed(1)} mi`;
    return `US$ ${number.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
}

function formatQty(value) {
    const number = Number(value);
    if (!Number.isFinite(number) || !number) return '--';
    return number.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: number >= 1 ? 6 : 8
    });
}

function formatQuote(value, quote = 'USDC') {
    const number = Number(value);
    if (!Number.isFinite(number)) return '--';
    const formatted = number.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: Math.abs(number) >= 1 ? 2 : 6
    });
    if (quote === 'USDC' || quote === 'USDT') return `US$ ${formatted}`;
    return `${formatted} ${quote}`;
}

function formatWhen(value) {
    const ms = Number(value);
    if (!ms) return '--';
    const date = new Date(ms);
    if (Number.isNaN(date.getTime())) return '--';
    return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
}

function signedQuote(value, quote = 'USDC') {
    const number = Number(value);
    if (!Number.isFinite(number)) return '--';
    const prefix = number > 0 ? '+' : '';
    return `${prefix}${formatQuote(number, quote)}`;
}

function numeric(value) {
    if (value == null || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function clampLevel(value) {
    const number = numeric(value);
    if (number == null) return 0.1;
    return Math.min(50, Math.max(0.1, Number(number.toFixed(2))));
}

function levelBasis(monitor) {
    return numeric(monitor?.entryValue)
        || numeric(monitor?.effectiveCapital)
        || numeric(monitor?.maxCapital);
}

function percentToUsd(percent, basis) {
    const perc = numeric(percent);
    const amount = numeric(basis);
    if (perc == null || !(amount > 0)) return null;
    return Number((amount * perc / 100).toFixed(2));
}

function usdToPercent(usd, basis) {
    const amount = numeric(usd);
    const entry = numeric(basis);
    if (!(entry > 0) || !(amount > 0)) return null;
    return clampLevel((amount / entry) * 100);
}

function withEditedLevels(monitor, targetProfit, stopLoss) {
    if (!monitor) return monitor;
    const buyPrice = numeric(monitor.buyPrice);
    const quantity = numeric(monitor.quantity);
    const entryValue = numeric(monitor.entryValue);
    const targetPrice = buyPrice ? buyPrice * (1 + targetProfit / 100) : null;
    const stopPrice = buyPrice ? buyPrice * (1 - stopLoss / 100) : null;
    const targetValue = quantity && targetPrice
        ? quantity * targetPrice
        : (entryValue ? entryValue * (1 + targetProfit / 100) : null);
    const stopValue = quantity && stopPrice
        ? quantity * stopPrice
        : (entryValue ? entryValue * (1 - stopLoss / 100) : null);
    const targetPnl = targetValue != null && entryValue != null ? targetValue - entryValue : null;
    const stopPnl = stopValue != null && entryValue != null ? stopValue - entryValue : null;
    return {
        ...monitor,
        targetProfit,
        stopLoss,
        targetPrice,
        stopPrice,
        targetValue,
        stopValue,
        targetPnl,
        stopPnl
    };
}

function positionAwareView(analysis, monitor, pair) {
    const technical = analysis?.signal || 'HOLD';
    const fallback = {
        signal: technical,
        signalLabel: analysis?.signalLabel || 'ESPERAR',
        summary: analysis?.summary || ''
    };

    const profile = resolveProfitProfile(monitor);
    const armLabel = `${formatProfitPct(profile.armProfit)}%`;
    const givebackLabel = `${formatProfitPct(profile.giveback)}%`;
    const inPosition = monitor?.status === 'IN_POSITION';
    const price = numeric(monitor?.currentPrice) || numeric(analysis?.price);
    const buyPrice = numeric(monitor?.buyPrice);
    const stopPrice = numeric(monitor?.stopPrice);
    const profitPerc = numeric(monitor?.profitPerc);
    const losing = (profitPerc != null && profitPerc < 0)
        || (buyPrice && price && price < buyPrice);
    const atStop = Boolean(stopPrice && price && price <= stopPrice);
    const trailArmed = Boolean(monitor?.trailArmed)
        || (profitPerc != null && profitPerc >= profile.armProfit)
        || (buyPrice && price && price >= buyPrice * (1 + profile.armProfit / 100));
    const lockAt = numeric(monitor?.trailFloorPerc) != null ? numeric(monitor.trailFloorPerc) : profile.armProfit;
    const gaveBack = Boolean(monitor?.trailArmed) && profitPerc != null && profitPerc < lockAt;
    const pnlText = profitPerc == null
        ? ''
        : ` (${profitPerc > 0 ? '+' : ''}${Number(profitPerc).toFixed(2)}%)`;
    const reversalText = reversalNeedsBoth(profile)
        ? ` MACD e EMA só vendem juntos depois de ${formatProfitPct(profile.reversalMinProfit)}%.`
        : ' Se MACD ou EMA reverterem, também vende.';

    if (!inPosition) {
        if (technical === 'SELL')
            return {
                signal: 'SELL',
                signalLabel: 'NÃO COMPRAR',
                summary: `Tendência de baixa em ${pair}. Isso não significa lucro — o monitor não deve comprar agora. Lucro ou stop só existem depois de uma compra.`
            };
        return fallback;
    }

    if (gaveBack)
        return {
            signal: 'SELL',
            signalLabel: `PISO ${lockAt.toFixed(1).replace('.', ',')}%`,
            summary: `Recuou ${givebackLabel} do pico${pnlText}. O piso móvel está armado — a venda automática deve executar agora.`
        };

    if (atStop)
        return {
            signal: 'STOPLOSS',
            signalLabel: 'STOP-LOSS',
            summary: `O preço chegou no stop-loss${pnlText}. A venda automática deve executar agora.`
        };

    if (trailArmed)
        return {
            signal: 'HOLD',
            signalLabel: 'EM LUCRO',
            summary: `Posição no lucro${pnlText}. O piso acompanha o pico (${givebackLabel} abaixo do topo). Se recuar isso, vende.${reversalText}`
        };

    if (losing)
        return {
            signal: 'HOLD',
            signalLabel: 'EM PREJUÍZO',
            summary: `Comprado a $${buyPrice ? Number(buyPrice).toFixed(2) : '--'}, agora${pnlText}. A venda automática só ocorre no stop-loss. Se chegar a ${armLabel}, o piso começa a acompanhar o pico.`
        };

    if (profitPerc != null && profitPerc > 0)
        return {
            signal: 'HOLD',
            signalLabel: 'EM LUCRO',
            summary: `Posição no lucro${pnlText}. Ainda abaixo de ${armLabel}. Quando passar, o piso acompanha o pico e não deixa devolver mais de ${givebackLabel} do topo.`
        };

    return {
        signal: 'HOLD',
        signalLabel: 'EM POSIÇÃO',
        summary: analysis?.summary
            || `Posição aberta${pnlText}. Stop-loss vale até passar de ${armLabel}. Depois o piso acompanha o pico.`
    };
}

function indicatorValue(analysis, key) {
    const ind = analysis?.indicators || {};
    if (key === 'rsi' && ind.rsi != null) return `RSI ${Number(ind.rsi).toFixed(1)}`;
    if (key === 'macd' && ind.macd != null) return `MACD ${Number(ind.macd).toFixed(4)}`;
    if (key === 'ema' && ind.ema20 != null && ind.ema50 != null)
        return `EMA 20 ${Number(ind.ema20).toFixed(2)} / EMA 50 ${Number(ind.ema50).toFixed(2)}`;
    if (key === 'bb' && ind.bbLower != null)
        return `Bandas ${Number(ind.bbLower).toFixed(2)} – ${Number(ind.bbUpper).toFixed(2)}`;
    return null;
}

function withPositionValues(monitor, livePrice) {
    if (!monitor || monitor.status !== 'IN_POSITION') return monitor;
    let buyPrice = numeric(monitor.buyPrice);
    const currentPrice = numeric(monitor.currentPrice) || numeric(livePrice);
    const targetProfit = numeric(monitor.targetProfit) || 0;
    const stopLoss = numeric(monitor.stopLoss) || 0;
    const profitPerc = numeric(monitor.profitPerc);
    let quantity = numeric(monitor.quantity);
    let entryValue = numeric(monitor.entryValue);

    if (!buyPrice && currentPrice && profitPerc != null && profitPerc > -100)
        buyPrice = currentPrice / (1 + profitPerc / 100);
    if (!quantity && entryValue && buyPrice)
        quantity = entryValue / buyPrice;
    if (!entryValue && quantity && buyPrice)
        entryValue = quantity * buyPrice;
    if (!buyPrice && quantity && entryValue)
        buyPrice = entryValue / quantity;

    const targetPrice = numeric(monitor.targetPrice) || (buyPrice ? buyPrice * (1 + targetProfit / 100) : null);
    const stopPrice = numeric(monitor.stopPrice) || (buyPrice ? buyPrice * (1 - stopLoss / 100) : null);
    const targetValue = numeric(monitor.targetValue)
        || (quantity && targetPrice ? quantity * targetPrice : (entryValue ? entryValue * (1 + targetProfit / 100) : null));
    const stopValue = numeric(monitor.stopValue)
        || (quantity && stopPrice ? quantity * stopPrice : (entryValue ? entryValue * (1 - stopLoss / 100) : null));
    const currentValue = numeric(monitor.currentValue)
        || (quantity && currentPrice ? quantity * currentPrice : null);
    const profitValue = numeric(monitor.profitValue)
        || (currentValue != null && entryValue != null ? currentValue - entryValue : null);
    const targetPnl = numeric(monitor.targetPnl)
        || (targetValue != null && entryValue != null ? targetValue - entryValue : null);
    const stopPnl = numeric(monitor.stopPnl)
        || (stopValue != null && entryValue != null ? stopValue - entryValue : null);

    return {
        ...monitor,
        buyPrice,
        currentPrice,
        quantity,
        entryValue,
        targetPrice,
        stopPrice,
        targetValue,
        stopValue,
        currentValue,
        profitValue,
        targetPnl,
        stopPnl
    };
}

function formatSupply(value) {
    const number = Number(value);
    if (!number) return '--';
    if (number >= 1e12) return `${(number / 1e12).toFixed(2)} tri`;
    if (number >= 1e9) return `${(number / 1e9).toFixed(2)} bi`;
    if (number >= 1e6) return `${(number / 1e6).toFixed(2)} mi`;
    return number.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
}

function apiError(err) {
    const data = err?.response?.data;
    if (typeof data === 'string') return data;
    if (data?.message) return data.message;
    return err?.message || 'Erro inesperado.';
}

function biasColor(bias) {
    if (bias === 'bullish') return PIE_COLORS.bullish;
    if (bias === 'bearish') return PIE_COLORS.bearish;
    return PIE_COLORS.neutral;
}

function ProfitMonitorDetail() {
    const { symbol } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const pairRaw = (symbol || '').toUpperCase();
    const pair = pairRaw.endsWith('USDT') ? `${pairRaw.slice(0, -4)}USDC` : pairRaw;
    const interval = INTERVALS.some(item => item.value === searchParams.get('interval'))
        ? searchParams.get('interval')
        : '15m';

    const [insight, setInsight] = useState(null);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);
    const [alreadyActive, setAlreadyActive] = useState(false);
    const [activeMonitor, setActiveMonitor] = useState(null);
    const [percentage, setPercentage] = useState(100);
    const [maxCapital, setMaxCapital] = useState(50);
    const [stopLoss, setStopLoss] = useState(2);
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [notification, setNotification] = useState({ type: '', text: '' });
    const [editingLevels, setEditingLevels] = useState(false);
    const [savingLevels, setSavingLevels] = useState(false);
    const [stopMode, setStopMode] = useState('percent');
    const [editStopLoss, setEditStopLoss] = useState(2);
    const [editStopUsd, setEditStopUsd] = useState('');

    const analysis = insight?.analysis;
    const asset = insight?.asset;

    useEffect(() => {
        if (!pairRaw.endsWith('USDT')) return undefined;
        const qs = searchParams.toString();
        navigate(`/profit-monitor/${pair}${qs ? `?${qs}` : ''}`, { replace: true });
        return undefined;
    }, [pairRaw, pair, navigate, searchParams]);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            setLoading(true);
            try {
                const [data, monitors] = await Promise.all([
                    getProfitMonitorInsight(pair, interval),
                    getProfitMonitors().catch(() => ({ rows: [] }))
                ]);
                if (cancelled) return;
                setInsight(data);
                const match = (monitors.rows || []).find(item => item.symbol === pair);
                setActiveMonitor(match || null);
                setAlreadyActive(Boolean(match && match.status !== 'STOPPED'));
            } catch (err) {
                if (!cancelled)
                    setNotification({ type: 'error', text: apiError(err) });
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        if (pair) load();
        return () => { cancelled = true; };
    }, [pair, interval]);

    useEffect(() => {
        if (!pair) return undefined;
        let cancelled = false;

        async function refreshMonitor() {
            try {
                const monitors = await getProfitMonitors();
                if (cancelled) return;
                const match = (monitors.rows || []).find(item => item.symbol === pair);
                setActiveMonitor(match || null);
                setAlreadyActive(Boolean(match && match.status !== 'STOPPED'));
            } catch (_) { /* ignore */ }
        }

        const timer = setInterval(refreshMonitor, 8000);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [pair]);

    useEffect(() => {
        if (!pair) return undefined;
        let cancelled = false;

        async function refreshInsight() {
            try {
                const data = await getProfitMonitorInsight(pair, interval);
                if (!cancelled) setInsight(data);
            } catch (_) { /* ignore */ }
        }

        const timer = setInterval(refreshInsight, 8000);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [pair, interval]);

    const indicatorSlices = useMemo(() => {
        return (analysis?.contributions || [])
            .filter(item => item.score)
            .map(item => ({
                key: item.key,
                label: item.label,
                value: Math.abs(item.score),
                color: biasColor(item.bias),
                caption: item.bias === 'bullish'
                    ? `+${item.score} · favorece compra`
                    : `${item.score} · favorece venda`
            }));
    }, [analysis]);

    const supplySlices = useMemo(() => {
        const circulating = Number(asset?.circulatingSupply || 0);
        const ceiling = Number(asset?.maxSupply || asset?.totalSupply || 0);
        if (!circulating || !ceiling || ceiling <= circulating) return [];
        return [
            {
                key: 'circulating',
                label: 'Em circulação',
                value: circulating,
                color: '#4caf50',
                caption: formatSupply(circulating)
            },
            {
                key: 'remaining',
                label: 'Ainda não emitido',
                value: ceiling - circulating,
                color: '#5c5470',
                caption: formatSupply(ceiling - circulating)
            }
        ];
    }, [asset]);

    const description = asset?.description || '';
    const shortDescription = description.length > 420 ? `${description.slice(0, 420).trim()}…` : description;

    async function onStart() {
        setStarting(true);
        try {
            await startProfitMonitor({
                symbol: pair,
                interval,
                percentage,
                maxCapital,
                targetProfit: 1.2,
                stopLoss
            });
            setAlreadyActive(true);
            setNotification({
                type: 'success',
                text: `Monitor iniciado para ${pair}. A compra automática ocorre quando o sinal técnico confirmar.`
            });
        } catch (err) {
            setNotification({ type: 'error', text: apiError(err) });
        } finally {
            setStarting(false);
        }
    }

    const positionMonitor = withPositionValues(activeMonitor, analysis?.price);
    const basis = levelBasis(positionMonitor);
    const canEditUsd = Boolean(basis > 0);
    const displayedPosition = editingLevels
        ? withEditedLevels(positionMonitor, 1.2, editStopLoss)
        : positionMonitor;

    function openLevelEditor() {
        const current = withPositionValues(activeMonitor, analysis?.price);
        const stop = clampLevel(current?.stopLoss || 2);
        const entry = levelBasis(current);
        setEditStopLoss(stop);
        setEditStopUsd(percentToUsd(stop, entry) ?? '');
        setStopMode('percent');
        setEditingLevels(true);
    }

    function onStopPercentChange(value) {
        const percent = clampLevel(value);
        setEditStopLoss(percent);
        const usd = percentToUsd(percent, basis);
        if (usd != null) setEditStopUsd(usd);
    }

    function onStopUsdChange(value) {
        setEditStopUsd(value);
        const percent = usdToPercent(value, basis);
        if (percent != null) setEditStopLoss(percent);
    }

    async function onSaveLevels() {
        if (!activeMonitor?.id) return;
        setSavingLevels(true);
        try {
            const updated = await updateProfitMonitor(activeMonitor.id, {
                targetProfit: 1.2,
                stopLoss: clampLevel(editStopLoss)
            });
            setActiveMonitor(updated);
            setEditingLevels(false);
            setNotification({
                type: 'success',
                text: `Stop-loss de ${pair} atualizado. O lucro continua livre após ${formatProfitPct(resolveProfitProfile(activeMonitor).armProfit)}%.`
            });
        } catch (err) {
            setNotification({ type: 'error', text: apiError(err) });
        } finally {
            setSavingLevels(false);
        }
    }
    const inPosition = positionMonitor?.status === 'IN_POSITION';
    const profitProfile = resolveProfitProfile(positionMonitor);
    const armLabel = `${formatProfitPct(profitProfile.armProfit)}%`;
    const givebackLabel = `${formatProfitPct(profitProfile.giveback)}%`;
    const trailOn = Boolean(positionMonitor?.trailArmed)
        || (positionMonitor?.profitPerc != null && positionMonitor.profitPerc >= profitProfile.armProfit);
    const reversalHint = reversalNeedsBoth(profitProfile)
        ? ` MACD e EMA só vendem juntos depois de ${formatProfitPct(profitProfile.reversalMinProfit)}%.`
        : ' MACD ou EMA também vendem se reverterem.';
    const quote = positionMonitor?.quoteAsset || 'USDC';
    const isBuy = analysis?.signal === 'BUY' || analysis?.automationReady;
    const view = isBuy && !inPosition
        ? {
            signal: 'BUY',
            signalLabel: 'COMPRAR',
            summary: analysis?.signal === 'BUY'
                ? analysis.summary
                : `Os indicadores apontam um momento favorável para comprar ${pair}.`
        }
        : positionAwareView(analysis, positionMonitor, pair);
    const signal = view.signal;
    const signalLabel = view.signalLabel;
    const summary = view.summary;
    const positionCard = inPosition ? (
        <section className="profit-card profit-position-card">
            <div className="profit-position-head">
                <h2>Detalhes da posição</h2>
                {!editingLevels && (
                    <button
                        type="button"
                        className="profit-edit-btn"
                        onClick={openLevelEditor}
                    >
                        Editar stop
                    </button>
                )}
            </div>
            <p className="profit-card-lead">
                Comprados {formatQty(positionMonitor.quantity)} {positionMonitor.baseAsset || pair.replace(/USDT|USDC$/, '')}
                {positionMonitor.buyPrice ? ` a $${formatPrice(positionMonitor.buyPrice)} cada` : ''}.
            </p>
            <div className="profit-position-grid">
                <div className="profit-position-box">
                    <span>Você pagou</span>
                    <strong>{formatQuote(positionMonitor.entryValue, quote)}</strong>
                    <small>
                        {formatQty(positionMonitor.quantity)} × ${formatPrice(positionMonitor.buyPrice)}
                    </small>
                </div>
                <div className={`profit-position-box ${positionMonitor.profitPerc > 0 ? 'profit' : positionMonitor.profitPerc < 0 ? 'loss' : ''}`}>
                    <span>Vale agora</span>
                    <strong>{formatQuote(positionMonitor.currentValue, quote)}</strong>
                    <small>
                        {positionMonitor.profitPerc > 0 ? '+' : ''}{positionMonitor.profitPerc ?? '--'}%
                        {positionMonitor.profitValue != null ? ` · ${signedQuote(positionMonitor.profitValue, quote)}` : ''}
                    </small>
                </div>
                <div className="profit-position-box profit">
                    <span>{trailOn
                        ? `Piso móvel (${positionMonitor.trailFloorPerc != null ? `${Number(positionMonitor.trailFloorPerc).toFixed(1).replace('.', ',')}%` : `${givebackLabel} abaixo do pico`})`
                        : `Lucro livre (mín. ${armLabel})`}</span>
                    <strong>{trailOn ? 'Acompanha o pico' : `Ainda abaixo de ${armLabel}`}</strong>
                    <small>
                        {trailOn
                            ? `Se recuar ${givebackLabel} do topo, vende.${reversalHint}`
                            : `Quando passar de ${armLabel}, o piso começa a acompanhar o pico. Stop de prejuízo só vale antes disso.`}
                    </small>
                </div>
                <div className="profit-position-box loss">
                    <span>{positionMonitor.trailArmed ? 'Piso na Binance' : `Vende no stop (${displayedPosition.stopLoss}%)`}</span>
                    <strong>${formatPrice(displayedPosition.stopPrice)}</strong>
                    <small>
                        Você recebe {formatQuote(displayedPosition.stopValue, quote)}
                        {displayedPosition.stopPnl != null ? ` (${signedQuote(displayedPosition.stopPnl, quote)})` : ''}
                    </small>
                </div>
            </div>
            {editingLevels && (
                <div className="profit-edit-levels">
                    <p className="profit-card-lead">
                        O lucro não tem teto. Depois de {armLabel} o piso acompanha o pico. Aqui você só ajusta o stop-loss (vale só antes de armar), em % ou em dólar.
                    </p>
                    <div className="profit-edit-grid">
                        <div className="profit-field">
                            <div className="profit-edit-label-row">
                                <label>Stop-loss</label>
                                <div className="profit-unit-toggle" role="group" aria-label="Unidade do stop-loss">
                                    <button
                                        type="button"
                                        className={stopMode === 'percent' ? 'active' : ''}
                                        onClick={() => setStopMode('percent')}
                                    >
                                        %
                                    </button>
                                    <button
                                        type="button"
                                        className={stopMode === 'usd' ? 'active' : ''}
                                        onClick={() => canEditUsd && setStopMode('usd')}
                                        disabled={!canEditUsd}
                                    >
                                        US$
                                    </button>
                                </div>
                            </div>
                            {stopMode === 'percent' ? (
                                <input
                                    type="number"
                                    className="form-control"
                                    min="0.1"
                                    max="50"
                                    step="0.1"
                                    value={editStopLoss}
                                    onChange={event => onStopPercentChange(event.target.value)}
                                    disabled={savingLevels}
                                />
                            ) : (
                                <input
                                    type="number"
                                    className="form-control"
                                    min="0.01"
                                    step="0.01"
                                    value={editStopUsd}
                                    onChange={event => onStopUsdChange(event.target.value)}
                                    disabled={savingLevels}
                                />
                            )}
                            <small>
                                {stopMode === 'percent'
                                    ? `≈ ${formatQuote(percentToUsd(editStopLoss, basis), quote)} de prejuízo máximo`
                                    : `≈ ${editStopLoss}% · vende a $${formatPrice(displayedPosition.stopPrice)}`}
                            </small>
                        </div>
                    </div>
                    <div className="profit-edit-actions">
                        <button
                            type="button"
                            className="profit-btn-secondary"
                            onClick={() => setEditingLevels(false)}
                            disabled={savingLevels}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="profit-btn profit-btn-inline"
                            onClick={onSaveLevels}
                            disabled={savingLevels}
                        >
                            {savingLevels ? 'Salvando...' : 'Salvar alterações'}
                        </button>
                    </div>
                </div>
            )}
        </section>
    ) : null;

    const cycles = Array.isArray(positionMonitor?.cycles) ? positionMonitor.cycles : (activeMonitor?.cycles || []);
    const closedCycles = Number(positionMonitor?.closedCycles ?? activeMonitor?.closedCycles) || cycles.length;
    const totalProfitValue = numeric(positionMonitor?.totalProfitValue ?? activeMonitor?.totalProfitValue);
    const totalProfitPerc = numeric(positionMonitor?.totalProfitPerc ?? activeMonitor?.totalProfitPerc);
    const realizedProfitValue = numeric(positionMonitor?.realizedProfitValue ?? activeMonitor?.realizedProfitValue);
    const realizedProfitPerc = numeric(positionMonitor?.realizedProfitPerc ?? activeMonitor?.realizedProfitPerc);
    const hasLifetime = Boolean(activeMonitor) && (
        closedCycles > 0 || totalProfitValue != null || realizedProfitValue != null || inPosition
    );
    const lifetimeTone = totalProfitValue > 0 ? 'profit' : totalProfitValue < 0 ? 'loss' : '';
    const lifetimeCard = hasLifetime ? (
        <section className="profit-card profit-lifetime-card">
            <h2>Resultado acumulado</h2>
            <p className="profit-card-lead">
                Soma de todas as compras e recompras deste monitor: ciclos já vendidos mais o resultado da posição aberta, se houver.
            </p>
            <div className="profit-position-grid profit-lifetime-grid">
                <div className={`profit-position-box ${lifetimeTone}`}>
                    <span>Total do monitor</span>
                    <strong>{signedQuote(totalProfitValue, quote)}</strong>
                    <small>
                        {totalProfitPerc > 0 ? '+' : ''}{totalProfitPerc ?? '--'}% sobre o capital usado nas compras
                    </small>
                </div>
                <div className={`profit-position-box ${realizedProfitValue > 0 ? 'profit' : realizedProfitValue < 0 ? 'loss' : ''}`}>
                    <span>Já realizado</span>
                    <strong>{signedQuote(realizedProfitValue, quote)}</strong>
                    <small>
                        {closedCycles} ciclo{closedCycles === 1 ? '' : 's'} fechado{closedCycles === 1 ? '' : 's'}
                        {realizedProfitPerc != null ? ` · ${realizedProfitPerc > 0 ? '+' : ''}${realizedProfitPerc}%` : ''}
                    </small>
                </div>
                <div className={`profit-position-box ${positionMonitor?.profitPerc > 0 ? 'profit' : positionMonitor?.profitPerc < 0 ? 'loss' : ''}`}>
                    <span>Posição atual</span>
                    <strong>{inPosition ? signedQuote(positionMonitor.profitValue, quote) : 'Sem posição'}</strong>
                    <small>
                        {inPosition
                            ? `${positionMonitor.profitPerc > 0 ? '+' : ''}${positionMonitor.profitPerc ?? '--'}% nesta compra`
                            : 'Aguardando a próxima compra'}
                    </small>
                </div>
            </div>
            {cycles.length > 0 && (
                <div className="profit-cycle-list">
                    {cycles.map((cycle, index) => (
                        <div
                            className={`profit-cycle-row ${cycle.profitValue > 0 ? 'profit' : cycle.profitValue < 0 ? 'loss' : ''}`}
                            key={`${cycle.buyAt}-${cycle.sellAt}-${index}`}
                        >
                            <div>
                                <strong>Ciclo {index + 1}</strong>
                                <small>
                                    {formatWhen(cycle.buyAt)} → {formatWhen(cycle.sellAt)}
                                </small>
                            </div>
                            <div>
                                Compra {formatQuote(cycle.buyValue, quote)}
                                {cycle.buyPrice ? ` a $${formatPrice(cycle.buyPrice)}` : ''}
                            </div>
                            <div>
                                Venda {formatQuote(cycle.sellValue, quote)}
                                {cycle.sellPrice ? ` a $${formatPrice(cycle.sellPrice)}` : ''}
                            </div>
                            <div className="profit-cycle-pnl">
                                {cycle.profitPerc > 0 ? '+' : ''}{cycle.profitPerc ?? '--'}%
                                {' · '}
                                {signedQuote(cycle.profitValue, quote)}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    ) : null;

    return (
        <>
            <Menu />
            <main className="content">
                <div className="profit-monitor profit-detail">
                    <div className="profit-detail-top">
                        <button type="button" className="profit-back" onClick={() => navigate('/profit-monitor')}>
                            ← Voltar ao monitor
                        </button>
                        <select
                            className="profit-select profit-detail-interval"
                            value={interval}
                            onChange={event => setSearchParams({ interval: event.target.value })}
                        >
                            {INTERVALS.map(item => (
                                <option key={item.value} value={item.value}>{item.label}</option>
                            ))}
                        </select>
                    </div>

                    {loading && <div className="profit-loading">Carregando {pair}...</div>}

                    {!loading && !analysis && !inPosition && !lifetimeCard && (
                        <div className="profit-empty">
                            Não foi possível carregar a análise de {pair}. Volte ao monitor e tente outro par.
                        </div>
                    )}

                    {!loading && !analysis && positionCard}
                    {!loading && !analysis && inPosition && (
                        <section className="profit-card">
                            <ProfitGoalBar analysis={analysis} monitor={positionMonitor} />
                        </section>
                    )}
                    {!loading && !analysis && lifetimeCard}

                    {!loading && analysis && (
                        <>
                            <header className="profit-detail-header">
                                <div className="profit-detail-identity">
                                    {asset?.image && (
                                        <img src={asset.image} alt={asset.name || pair} className="profit-detail-logo" />
                                    )}
                                    <div>
                                        <h1>{asset?.name || pair.replace('USDC', '')}</h1>
                                        <p>
                                            {pair.replace('USDC', '/USDC')}
                                            {asset?.marketCapRank ? ` · #${asset.marketCapRank} em market cap` : ''}
                                            {asset?.categories?.length ? ` · ${asset.categories[0]}` : ''}
                                        </p>
                                    </div>
                                </div>
                                <div className={`profit-signal ${signal}`}>
                                    <div>
                                        <div className="profit-signal-label">{signalLabel}</div>
                                        <small>{summary}</small>
                                    </div>
                                    <div className="profit-price">
                                        <strong>${formatPrice(analysis.price)}</strong>
                                        <span>
                                            {analysis.changePerc > 0 ? '+' : ''}
                                            {analysis.changePerc ?? '--'}% recente
                                        </span>
                                    </div>
                                </div>
                            </header>

                            {positionCard}
                            <section className="profit-card">
                                <ProfitGoalBar analysis={analysis} monitor={positionMonitor} />
                            </section>
                            {lifetimeCard}

                            <section className="profit-detail-pies">
                                <div className="profit-card">
                                    <h2>Por que o sinal agora</h2>
                                    <p className="profit-card-lead">
                                        {isBuy && (analysis.score || 0) < 40
                                            ? 'O sinal verde da lista segue a regra de compra (MACD de alta, RSI abaixo de 65 e EMAs de alta), mesmo se o score ainda não chega a 40. Fatia verde favorece compra; vermelha, espera ou venda.'
                                            : 'O tamanho da fatia é o peso do indicador. Verde favorece compra; vermelho favorece espera ou venda.'}
                                    </p>
                                    <ProfitPieChart
                                        slices={indicatorSlices}
                                        colorKey={INDICATOR_PIE_KEY}
                                        centerTitle="Score"
                                        centerValue={analysis.score}
                                        emptyLabel="Os indicadores estão neutros neste intervalo."
                                    />
                                </div>
                                <div className="profit-card">
                                    <h2>Oferta do ativo</h2>
                                    <p className="profit-card-lead">
                                        Disco da oferta: quanto já circula no mercado e quanto ainda pode ser emitido.
                                    </p>
                                    <ProfitPieChart
                                        slices={supplySlices}
                                        centerTitle="Circulante"
                                        centerValue={
                                            asset?.maxSupply || asset?.totalSupply
                                                ? `${(((asset.circulatingSupply || 0) / (asset.maxSupply || asset.totalSupply)) * 100).toFixed(0)}%`
                                                : '--'
                                        }
                                        emptyLabel="Este ativo não informa oferta máxima para montar o gráfico."
                                    />
                                </div>
                            </section>

                            <section className="profit-detail-grid">
                                <div className="profit-card">
                                    <h2>Informações do ativo</h2>
                                    {description ? (
                                        <>
                                            <p className="profit-asset-desc">
                                                {showFullDescription ? description : shortDescription}
                                            </p>
                                            {description.length > 420 && (
                                                <button
                                                    type="button"
                                                    className="profit-link-btn"
                                                    onClick={() => setShowFullDescription(current => !current)}
                                                >
                                                    {showFullDescription ? 'Mostrar menos' : 'Ler mais'}
                                                </button>
                                            )}
                                        </>
                                    ) : (
                                        <p className="profit-empty" style={{ textAlign: 'left', padding: '0 0 12px' }}>
                                            Sem descrição pública para este ativo no momento.
                                        </p>
                                    )}
                                    <div className="profit-indicators profit-asset-stats">
                                        <div className="profit-indicator">
                                            <span>Market cap</span>
                                            <strong>{formatCompactUsd(asset?.marketCap)}</strong>
                                        </div>
                                        <div className="profit-indicator">
                                            <span>Volume 24h</span>
                                            <strong>{formatCompactUsd(asset?.volume)}</strong>
                                        </div>
                                        <div className="profit-indicator">
                                            <span>Máxima 24h</span>
                                            <strong>${formatPrice(asset?.high24h)}</strong>
                                        </div>
                                        <div className="profit-indicator">
                                            <span>Mínima 24h</span>
                                            <strong>${formatPrice(asset?.low24h)}</strong>
                                        </div>
                                        <div className="profit-indicator">
                                            <span>ATH</span>
                                            <strong>${formatPrice(asset?.ath)}</strong>
                                        </div>
                                        <div className="profit-indicator">
                                            <span>Variação 7d</span>
                                            <strong>
                                                {asset?.change7d == null
                                                    ? '--'
                                                    : `${asset.change7d > 0 ? '+' : ''}${asset.change7d}%`}
                                            </strong>
                                        </div>
                                    </div>
                                    {asset?.homepage && (
                                        <a className="profit-link-btn" href={asset.homepage} target="_blank" rel="noreferrer">
                                            Site oficial
                                        </a>
                                    )}
                                </div>

                                <div className="profit-card">
                                    <h2>Leitura dos indicadores</h2>
                                    <p className="profit-card-lead">{summary}</p>
                                    {(analysis.contributions || []).map(item => (
                                        <div className={`profit-trend-item ${item.bias}`} key={item.key}>
                                            <div className="profit-trend-head">
                                                <strong>{item.label}</strong>
                                                <span>{item.score > 0 ? `+${item.score}` : item.score}</span>
                                            </div>
                                            <p>{item.reason || INDICATOR_HELP[item.key]}</p>
                                            <small>
                                                {[indicatorValue(analysis, item.key), INDICATOR_HELP[item.key]]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </small>
                                        </div>
                                    ))}
                                    {analysis.automationReady && (
                                        <div className="profit-trend-ready">
                                            Este par já atende a regra de compra automática: MACD de alta, RSI abaixo de 65 e EMAs de alta.
                                        </div>
                                    )}
                                </div>
                            </section>

                            {inPosition ? (
                                <section className="profit-card profit-detail-start">
                                    <h2>Posição em andamento</h2>
                                    <p className="profit-card-lead" style={{ marginBottom: 0 }}>
                                        Este monitor já comprou {pair.replace(/USDT|USDC$/, '')}. {profitModeHelp(positionMonitor)}
                                    </p>
                                </section>
                            ) : (
                            <section className="profit-card profit-detail-start">
                                <h2>Iniciar monitor neste ativo</h2>
                                <div className="profit-detail-sliders">
                                    <div className="profit-field">
                                        <label>Capital máximo (USDC): <strong>{maxCapital}</strong></label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            min="1"
                                            max="1000000"
                                            step="1"
                                            value={maxCapital}
                                            onChange={event => setMaxCapital(Math.max(0, parseFloat(event.target.value) || 0))}
                                            disabled={starting || alreadyActive}
                                        />
                                    </div>
                                    <div className="profit-field">
                                        <label>
                                            Uso do capital: <strong>{percentage}%</strong>
                                            {' '}→ ~{(maxCapital * percentage / 100).toFixed(2)} por compra
                                        </label>
                                        <input
                                            type="range"
                                            className="form-range"
                                            min="1"
                                            max="100"
                                            value={percentage}
                                            onChange={event => setPercentage(parseFloat(event.target.value))}
                                            disabled={starting || alreadyActive}
                                        />
                                    </div>
                                    <div className="profit-field">
                                        <label>Stop loss: <strong>{stopLoss}%</strong></label>
                                        <input
                                            type="range"
                                            className="form-range"
                                            min="0.1"
                                            max="20"
                                            step="0.1"
                                            value={stopLoss}
                                            onChange={event => setStopLoss(parseFloat(event.target.value))}
                                            disabled={starting || alreadyActive}
                                        />
                                    </div>
                                </div>
                                <button
                                    className="profit-btn"
                                    type="button"
                                    onClick={onStart}
                                    disabled={starting || alreadyActive}
                                >
                                    {alreadyActive ? 'Já monitorada' : starting ? 'Iniciando...' : `Iniciar monitor de ${pair.replace('USDC', '/USDC')}`}
                                </button>
                            </section>
                            )}
                        </>
                    )}
                </div>
            </main>
            <Footer />
            {notification.text && (
                <Toast
                    type={notification.type}
                    text={notification.text}
                    onClose={() => setNotification({ type: '', text: '' })}
                />
            )}
        </>
    );
}

export default ProfitMonitorDetail;
